/**
 * Default: Components / Overwrites / Bootstrap > Modal
 *
 * @copyright 2024 i-fabrik GmbH
 * @author Heiko Pfefferkorn
 */

import {getSelector} from '@ifabnpm/bootstrap/utils.js';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import EventHandler from '@ifabnpm/bootstrap/dom/event-handler.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';
import Data from '@ifabnpm/bootstrap/dom/data.js';

// -------
// Private
// -------

const NAME      = 'modal';
const DATA_KEY  = `ifab.${NAME}`;
const EVENT_KEY = `.${DATA_KEY}`;

const copyContents = (source, target) => {
	const sourceNodes = SelectorEngine.children(source, '*');
	const sourceText  = (sourceNodes.length <= 0) ? source.textContent : '';

	if (sourceNodes.length > 0 || sourceText) {
		if (sourceNodes.length > 0) {
			for (const sourceNode of sourceNodes) {
				target.append(sourceNode.cloneNode(true));
				// Manipulator.elementAppend(sourceNode, target);
			}
		} else {
			target.textContent = sourceText;
		}
	}
};

const manipulateTrigger = (elements, flag) => {
	for (const element of elements) {
		if(flag) {
			Manipulator.addClass(element, '_active');
		} else {
			Manipulator.removeClass(element, '_active');
		}
	}
};

const modalIframe = () => {
	const container = SelectorEngine.findOne('#modal-iframe');
	const controls  = (container) ? SelectorEngine.find('[data-modal-trigger="iframe"]') : null;

	if (!controls) {
		return;
	}

	const modal     = new bootstrap.Modal(container);
	const modalTitle = SelectorEngine.findOne('[data-modal-title]', container);
	const modalBody = SelectorEngine.findOne('.modal-body', container);

	// Modal wird geöffnet, Daten eintragen.
	container.addEventListener('show.bs.modal', (event) => {
		const trigger   = event.relatedTarget;
		const uri       = Data.get(trigger, `${DATA_KEY}.uri`);
		const context   = Data.get(trigger, `${DATA_KEY}.context`);
		const header    = Data.get(trigger, `${DATA_KEY}.header`);
		const modalType = trigger.dataset.modalType;
		const iframe    = Manipulator.elementPrepend(`<iframe src="${uri}" scrolling="yes"></iframe>`, modalBody);

		container.dataset.modalContext = context;

		if (modalType !== undefined && modalType !== '') {
			container.dataset.modalType = modalType;
		}

		Data.set(container, `${DATA_KEY}.iframe`, iframe);
		Data.set(event.target, `${DATA_KEY}.currentTrigger`, trigger);

		Manipulator.addClass(trigger, '_active');

		manipulateTrigger(controls, true);

		if (modalTitle) {
			modalTitle.innerHTML = header;
		}

		window.pageInstance.currentIframeModal = iframe;

		// iframe.height = iframe.contentWindow.document.body.scrollHeight;
	});

	// Modal wurde geschlossen, Daten entfernen.
	container.addEventListener('hidden.bs.modal', (event) => {
		const trigger = Data.get(event.target, `${DATA_KEY}.currentTrigger`);
		const iframe  = Data.get(container, `${DATA_KEY}.iframe`);

		if (modalTitle) {
			modalTitle.textContent = '';
		}

		delete container.dataset.modalContext;
		delete container.dataset.modalType;

		iframe.remove();

		window.pageInstance.currentIframeModal = null;

		manipulateTrigger(controls, false);

		// Set focus back to trigger.
		if (trigger) {
			setTimeout(() => trigger.focus());
		}
	});

	// Modal trigger holen und initialisieren.
	for (const control of controls) {
		const uri = control.dataset.modalUri;

		if (uri) {
			Data.set(control, `${DATA_KEY}.uri`, uri);
			Data.set(control, `${DATA_KEY}.header`, control.dataset.modalHeader?? '');
			Data.set(control, `${DATA_KEY}.context`, control.dataset.modalContext?? 'none');

			EventHandler.on(control, `click${EVENT_KEY}`, (event) => {
				event.preventDefault();

				modal.show(event.delegateTarget);
			});

			delete control.dataset.modalContext;
			delete control.dataset.modalHeader;
			delete control.dataset.modalUri;
		} else {
			Manipulator.setDisabled(control);
		}
	}
};


const modalInlineBindTrigger = (element) => {
	const selector = getSelector(element);
	const target   = (selector) ? SelectorEngine.findOne(selector) : null;

	if (target) {
		Data.set(element, `${DATA_KEY}.target`, target);
		Data.set(element, `${DATA_KEY}.context`, element.dataset.modalContext ?? '');
		Data.set(element, `${DATA_KEY}.type`, element.dataset.modalType ?? '');

		EventHandler.on(element, `click${EVENT_KEY}`, (event) => {
			event.preventDefault();

			Data.set(window.pageInstance.modalInlineContainer, `${DATA_KEY}.modalClass`, (element.dataset.modalClass ?? ''));

			window.pageInstance.modalInline.show(event.delegateTarget);
		});
	} else {
		Manipulator.setDisabled(element);
	}
};

const modalInline = () => {
	const container = SelectorEngine.findOne('#modal-inline');
	const controls  = SelectorEngine.find('[data-trigger-modal="inline"]');

	if (container) {
		window.pageInstance.modalInlineContainer = container;

		if (!window.pageInstance.modalInline) {
			const modal = new bootstrap.Modal(container);
			const body   = SelectorEngine.findOne('[data-modal-body]', container);
			const title = SelectorEngine.findOne('[data-modal-title]', container);

			// Modal wird geöffnet, Daten eintragen.
			container.addEventListener('show.bs.modal', (event) => {
				const trigger      = event.relatedTarget;
				const target       = Data.get(trigger, `${DATA_KEY}.target`);
				const modalContext = Data.get(trigger, `${DATA_KEY}.context`);
				const modalType    = Data.get(trigger, `${DATA_KEY}.type`);

				if (modalType !== undefined && modalType !== '') {
					container.dataset.modalType = modalType;
				}

				if (modalContext !== undefined && modalContext !== '') {
					container.dataset.modalContext = modalContext;
				}

				Manipulator.addClass(trigger, '_active');

				copyContents(target, body);

				title.innerHTML = trigger.dataset.modalTitle ?? trigger.getAttribute('title');

				Data.set(event.target, `${DATA_KEY}.currentTrigger`, trigger);
			});

			// Modal wurde geschlossen, Daten entfernen.
			container.addEventListener('hide.bs.modal', (event) => {
				const trigger = Data.get(event.target, `${DATA_KEY}.currentTrigger`);

				// Set focus back to trigger.
				if (trigger) {
					Manipulator.removeClass(trigger, '_active');

					EventHandler.trigger(trigger, `closed${EVENT_KEY}`);

					setTimeout(() => trigger.focus());
				}
			});

			// Modal wurde geschlossen, Daten entfernen.
			container.addEventListener('hidden.bs.modal', (event) => {
				title.textContent = '';
				body.textContent  = '';

				delete container.dataset.modalContext;
				delete container.dataset.modalType;
			});

			window.pageInstance.modalInline = modal;
		}

		// Modal trigger holen und initialisieren.
		for (const control of controls) {
			modalInlineBindTrigger(control);
		}
	}
};

// ------
// Public
// ------

/**
 * ´Modal´-Elemente initialisieren.
 *
 * @returns {HTMLElement|Array}
 */
const init = () => {
	// Ist der aktuelle Kontext des Dokumentes ´modal´ (also ein Iframe), dann muss die aktuelle Höhe des Dokumentes an
	// das aktive Modal in `window.parent` übergeben werden.
	if (Manipulator.getDataAttribute(document.body, 'page-type') === 'modal') {
		const currentModal = window.parent.pageInstance.currentIframeModal;
		const container    = SelectorEngine.findOne('.page-main');

		if (currentModal) {
			const updateHeight = () => {
				setTimeout(() => {
					currentModal.style.setProperty(`--_modal-iframe-doc-scroll-height`, `${document.body.scrollHeight}px`);
				}, 50);
			};

			const modalResizeObserver = new ResizeObserver(entries => {
				if (entries[0].target) {
					updateHeight();
				}
			});

			modalResizeObserver.observe(container);

			updateHeight();
		}
	}

	modalInline();
	modalIframe();
};

// Export
export default init;
